import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Layers, Plus } from '@/lib/icons';
import { platformApi } from '@/features/platform/api';
import { useToast } from '@/hooks/use-toast';
import {
  Card, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/_layout/settings/platform/products')({
  component: PlatformProductsPage,
});

async function fetchProducts() {
  try {
    const resp = await platformApi.get('/api/v1/products');
    const data = resp.data?.value ?? resp.data;
    return Array.isArray(data) ? data : (data?.items ?? []);
  } catch { return []; }
}

async function createProduct(data: { code: string; name: string; capability: string }) {
  const resp = await platformApi.post('/api/v1/products', data);
  return resp.data?.value ?? resp.data;
}

function PlatformProductsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['platform-products'],
    queryFn: fetchProducts,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [capability, setCapability] = useState('');

  const createMutation = useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-products'] });
      setDialogOpen(false);
      setCode('');
      setName('');
      setCapability('');
      toast({ title: 'Product created', description: 'New product added to the catalog.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create product.', variant: 'destructive' });
    },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Product Catalog</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform-wide product definitions that can be entitled to tenants.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="h-9">
              <Plus className="h-4 w-4 mr-1.5" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Product</DialogTitle>
              <DialogDescription>
                Define a new product that can be entitled to tenants.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Product Code</label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. device_financing" className="mt-1.5 font-mono" />
              </div>
              <div>
                <label className="text-sm font-medium">Display Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Device Financing" className="mt-1.5" />
              </div>
              <div>
                <label className="text-sm font-medium">Capability</label>
                <Input value={capability} onChange={(e) => setCapability(e.target.value)} placeholder="e.g. bnpl, insurance, savings" className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                disabled={!code.trim() || !name.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate({ code: code.trim(), name: name.trim(), capability: capability.trim() })}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Product'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Layers className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No products defined yet</p>
              <p className="text-xs mt-1">Add your first product to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Capability</TableHead>
                  <TableHead className="hidden md:table-cell">Config</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p: any) => (
                  <TableRow key={p.id ?? p.code}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {p.code}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.capability}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground font-mono">
                      {p.defaultConfig ? JSON.stringify(p.defaultConfig).slice(0, 50) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
